import {
    APP_INITIALIZER,
    type EnvironmentProviders,
    makeEnvironmentProviders,
} from '@angular/core';
import {
    BrandCollection,
    CustomReportCollection,
    type IAppointment,
    type IBrand,
    type ICustomReport,
    type IInvoice,
    type IMarketingCampaign,
    type IMarketingCampaignEntry,
    type IMarketingCampaignRun,
    type IPatient,
    type IPractice,
    type IReportRun,
    type IStaffer,
    type ITreatmentPlan,
    type ITreatmentStep,
    MarketingCampaignCollection,
    PatientCollection,
    isAppointment,
    isBrand,
    isPatient,
    isPractice,
    isStaffer,
    isTreatmentPlan,
    isTreatmentStep,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  type WithRef,
  docIsFromCollection,
  isObject,
  isWithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { ManagementToolRegistryService } from './management-tool-registry.service';
import {
    type IManagementResource,
    type IRelatedResource,
    ManagementResource,
} from './models/management-resource';
import { AuthUserLookupTool } from './tools/auth-user-lookup.tool';
import { BridgeDiagnosticsTool } from './tools/bridge-config-conflicts.tool';
import { CampaignCostForecastTool } from './tools/campaign-cost-forecast.tool';
import { CopyShareableLinkTool } from './tools/copy-shareable-link.tool';
import { DeduplicatePatientMediaTool } from './tools/deduplicate-patient-media.tool';
import { GenerateInvoiceForAppointmentTool } from './tools/generate-invoice-for-appointment.tool';
import { NavigateMigrationsTool } from './tools/navigate-migrations.tool';
import { NavigateOrganisationsTool } from './tools/navigate-organisations.tool';
import { NavigatePartnersTool } from './tools/navigate-partners.tool';
import { NavigateToolsTool } from './tools/navigate-tools.tool';
import { OpenInFirestoreTool } from './tools/open-in-firestore.tool';
import { OpenReportRunStorageTool } from './tools/open-report-run-storage.tool';
import { OpenStorageBucketTool } from './tools/open-storage-bucket.tool';
import { OvieHealthDataTool } from './tools/ovie-health-data.tool';
import { PatientDetailsHistoryTool } from './tools/patient-details-history.tool';
import { RecalculateAccountSummaryTool } from './tools/recalculate-account-summary.tool';
import { FeeScheduleCleanupTool } from './tools/fee-schedule-cleanup.tool';
import { RestoreDeletedMediaTool } from './tools/restore-deleted-media.tool';
import { ViewDataTool } from './tools/view-data.tool';

function resolveRefPath<T>(value: WithRef<T>): string {
  return value.ref.path;
}

const PATIENT_RESOURCE: IManagementResource<WithRef<IPatient>> = {
  name: 'patient',
  label: 'Patient',
  icon: 'person',
  resource: ManagementResource.Patient,
  typeGuard: (data: unknown): data is WithRef<IPatient> =>
    isWithRef(data) && isPatient(data),
  resolveLabel: (value): string => {
    return value.name;
  },
  uniqueBy: resolveRefPath,
};

const INVOICE_RESOURCE: IManagementResource<WithRef<IInvoice>> = {
  name: 'invoice',
  label: 'Invoice',
  icon: 'receipt',
  resource: ManagementResource.Invoice,
  typeGuard: (data: unknown): data is WithRef<IInvoice> =>
    isWithRef(data) && data.ref.path.includes(PatientCollection.Invoices),
  resolveLabel: (value): string => {
    return value.reference;
  },
  uniqueBy: resolveRefPath,
};

const APPOINTMENT_RESOURCE: IManagementResource<WithRef<IAppointment>> = {
  name: 'appointment',
  label: 'Appointment',
  icon: 'event',
  resource: ManagementResource.Appointment,
  typeGuard: (data: unknown): data is WithRef<IAppointment> =>
    isWithRef(data) &&
    isAppointment(data) &&
    data.ref.path.includes(PatientCollection.Appointments),
  resolveLabel: (value): string => {
    return value.treatmentPlan?.treatmentStep?.name ?? 'Appointment';
  },
  uniqueBy: resolveRefPath,
  resolveRelated: async (value): Promise<IRelatedResource<unknown>[]> => {
    if (!value.invoiceRef) {
      return [];
    }

    const treatmentStep = value.treatmentPlan
      ? await Firestore.getDoc(value.treatmentPlan.treatmentStep.ref)
      : undefined;
    const treatmentStepResource = treatmentStep
      ? {
          resource: ManagementResource.TreatmentStep,
          value: treatmentStep,
        }
      : undefined;

    const invoice = value.invoiceRef
      ? await Firestore.getDoc(value.invoiceRef)
      : undefined;
    const invoiceResource = invoice
      ? { resource: ManagementResource.Invoice, value: invoice }
      : undefined;

    return compact([treatmentStepResource, invoiceResource]);
  },
};

const TREATMENT_PLAN_RESOURCE: IManagementResource<WithRef<ITreatmentPlan>> = {
  name: 'treatmentPlan',
  label: 'Treatment Plan',
  icon: 'assignment',
  resource: ManagementResource.TreatmentPlan,
  typeGuard: (data: unknown): data is WithRef<ITreatmentPlan> =>
    isWithRef(data) && isTreatmentPlan(data),
  resolveLabel: (value): string => {
    return value.name;
  },
  uniqueBy: resolveRefPath,
};

const TREATMENT_STEP_RESOURCE: IManagementResource<WithRef<ITreatmentStep>> = {
  name: 'treatmentStep',
  label: 'Treatment Step',
  icon: 'checklist',
  resource: ManagementResource.TreatmentStep,
  typeGuard: (data: unknown): data is WithRef<ITreatmentStep> =>
    isWithRef(data) && isTreatmentStep(data),
  resolveLabel: (value): string => {
    return value.name;
  },
  uniqueBy: resolveRefPath,
};

const MARKETING_CAMPAIGN_RESOURCE: IManagementResource<
  WithRef<IMarketingCampaign>
> = {
  name: 'marketingCampaign',
  label: 'Campaign',
  icon: 'campaign',
  resource: ManagementResource.MarketingCampaign,
  typeGuard: (data: unknown): data is WithRef<IMarketingCampaign> =>
    isWithRef(data) &&
    docIsFromCollection(data, BrandCollection.MarketingCampaigns),
  resolveLabel: (value): string => {
    return value.name;
  },
  uniqueBy: resolveRefPath,
};

const BRAND_RESOURCE: IManagementResource<WithRef<IBrand>> = {
  name: 'brand',
  label: 'Brand',
  icon: 'branding_watermark',
  resource: ManagementResource.Brand,
  typeGuard: (data: unknown): data is WithRef<IBrand> =>
    isWithRef(data) && isBrand(data) && !isPractice(data),
  resolveLabel: (value): string => {
    return value.name;
  },
  uniqueBy: resolveRefPath,
};

const MARKETING_CAMPAIGN_RUN_RESOURCE: IManagementResource<
  WithRef<IMarketingCampaignRun>
> = {
  name: 'marketingCampaignRun',
  label: 'Campaign Run',
  icon: 'play_circle',
  resource: ManagementResource.MarketingCampaignRun,
  typeGuard: (data: unknown): data is WithRef<IMarketingCampaignRun> =>
    isWithRef(data) &&
    docIsFromCollection(
      data,
      MarketingCampaignCollection.MarketingCampaignRuns
    ),
  resolveLabel: (value): string => {
    return `${value.type} - ${value.status}`;
  },
  uniqueBy: resolveRefPath,
};

const PRACTICE_RESOURCE: IManagementResource<WithRef<IPractice>> = {
  name: 'practice',
  label: 'Practice',
  icon: 'business',
  resource: ManagementResource.Practice,
  typeGuard: (data: unknown): data is WithRef<IPractice> =>
    isWithRef(data) && isPractice(data),
  resolveLabel: (value): string => {
    return value.name;
  },
  uniqueBy: resolveRefPath,
};

const MARKETING_CAMPAIGN_ENTRY_RESOURCE: IManagementResource<
  WithRef<IMarketingCampaignEntry>
> = {
  name: 'marketingCampaignEntry',
  label: 'Campaign Entry',
  icon: 'person_pin',
  resource: ManagementResource.MarketingCampaignEntry,
  typeGuard: (data: unknown): data is WithRef<IMarketingCampaignEntry> =>
    isWithRef(data) &&
    docIsFromCollection(
      data,
      MarketingCampaignCollection.MarketingCampaignEntries
    ),
  resolveLabel: (value): string => {
    return value.patient.name;
  },
  uniqueBy: resolveRefPath,
};

const STAFFER_RESOURCE: IManagementResource<WithRef<IStaffer>> = {
  name: 'staffer',
  label: 'Staffer',
  icon: 'badge',
  resource: ManagementResource.Staffer,
  typeGuard: (data: unknown): data is WithRef<IStaffer> => isStaffer(data),
  resolveLabel: (value): string => {
    return value.user.name;
  },
  uniqueBy: resolveRefPath,
};

const CUSTOM_REPORT_RESOURCE: IManagementResource<WithRef<ICustomReport>> = {
  name: 'customReport',
  label: 'Custom Report',
  icon: 'analytics',
  resource: ManagementResource.CustomReport,
  typeGuard: (data: unknown): data is WithRef<ICustomReport> =>
    isWithRef(data) && docIsFromCollection(data, BrandCollection.CustomReports),
  resolveLabel: (value): string => {
    return value.name;
  },
  uniqueBy: resolveRefPath,
};

const REPORT_RUN_RESOURCE: IManagementResource<WithRef<IReportRun>> = {
  name: 'reportRun',
  label: 'Report Run',
  icon: 'history',
  resource: ManagementResource.ReportRun,
  typeGuard: (data: unknown): data is WithRef<IReportRun> =>
    isWithRef(data) &&
    docIsFromCollection(data, CustomReportCollection.ReportRuns),
  resolveLabel: (value): string => {
    return value.runAt.toDate().toLocaleString();
  },
  uniqueBy: resolveRefPath,
};

const WITH_REF_RESOURCE: IManagementResource<WithRef<object>> = {
  name: 'withRef',
  label: 'Document',
  icon: 'description',
  resource: ManagementResource.WithRef,
  hideInSidebar: true,
  typeGuard: (data: unknown): data is WithRef<object> => isWithRef(data),
  resolveLabel: (value): string => {
    return value.ref.path;
  },
};

const DATA_RESOURCE: IManagementResource<object> = {
  name: 'data',
  label: 'Data',
  icon: 'data_object',
  resource: ManagementResource.Data,
  hideInSidebar: true,
  typeGuard: (data: unknown): data is object => isObject(data),
  resolveLabel: (): string => 'Data',
};

function initManagementTools(
  registry: ManagementToolRegistryService,
  navigateOrganisations: NavigateOrganisationsTool,
  navigatePartners: NavigatePartnersTool,
  navigateMigrations: NavigateMigrationsTool,
  navigateTools: NavigateToolsTool,
  authUserLookup: AuthUserLookupTool,
  recalculateAccountSummary: RecalculateAccountSummaryTool,
  generateInvoice: GenerateInvoiceForAppointmentTool,
  restoreDeletedMedia: RestoreDeletedMediaTool,
  deduplicatePatientMedia: DeduplicatePatientMediaTool,
  patientDetailsHistory: PatientDetailsHistoryTool,
  viewData: ViewDataTool,
  ovieHealthData: OvieHealthDataTool,
  openInFirestore: OpenInFirestoreTool,
  campaignCostForecast: CampaignCostForecastTool,
  openStorageBucket: OpenStorageBucketTool,
  feeScheduleCleanup: FeeScheduleCleanupTool,
  openReportRunStorage: OpenReportRunStorageTool,
  bridgeDiagnostics: BridgeDiagnosticsTool,
  copyShareableLink: CopyShareableLinkTool
): () => void {
  return () => {
    registry.registerResource(PATIENT_RESOURCE);
    registry.registerResource(INVOICE_RESOURCE);
    registry.registerResource(APPOINTMENT_RESOURCE);
    registry.registerResource(TREATMENT_PLAN_RESOURCE);
    registry.registerResource(TREATMENT_STEP_RESOURCE);
    registry.registerResource(MARKETING_CAMPAIGN_RESOURCE);
    registry.registerResource(MARKETING_CAMPAIGN_RUN_RESOURCE);
    registry.registerResource(MARKETING_CAMPAIGN_ENTRY_RESOURCE);
    registry.registerResource(BRAND_RESOURCE);
    registry.registerResource(PRACTICE_RESOURCE);
    registry.registerResource(STAFFER_RESOURCE);
    registry.registerResource(CUSTOM_REPORT_RESOURCE);
    registry.registerResource(REPORT_RUN_RESOURCE);
    registry.registerResource(WITH_REF_RESOURCE);
    registry.registerResource(DATA_RESOURCE);

    registry.registerTool(navigateOrganisations);
    registry.registerTool(navigatePartners);
    registry.registerTool(navigateMigrations);
    registry.registerTool(navigateTools);
    registry.registerTool(authUserLookup);
    registry.registerTool(recalculateAccountSummary);
    registry.registerTool(generateInvoice);
    registry.registerTool(restoreDeletedMedia);
    registry.registerTool(deduplicatePatientMedia);
    registry.registerTool(patientDetailsHistory);
    registry.registerTool(viewData);
    registry.registerTool(ovieHealthData);
    registry.registerTool(openInFirestore);
    registry.registerTool(campaignCostForecast);
    registry.registerTool(openStorageBucket);
    registry.registerTool(feeScheduleCleanup);
    registry.registerTool(openReportRunStorage);
    registry.registerTool(bridgeDiagnostics);
    registry.registerTool(copyShareableLink);
  };
}

export function provideManagementTools(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: APP_INITIALIZER,
      useFactory: initManagementTools,
      deps: [
        ManagementToolRegistryService,
        NavigateOrganisationsTool,
        NavigatePartnersTool,
        NavigateMigrationsTool,
        NavigateToolsTool,
        AuthUserLookupTool,
        RecalculateAccountSummaryTool,
        GenerateInvoiceForAppointmentTool,
        RestoreDeletedMediaTool,
        DeduplicatePatientMediaTool,
        PatientDetailsHistoryTool,
        ViewDataTool,
        OvieHealthDataTool,
        OpenInFirestoreTool,
        CampaignCostForecastTool,
        OpenStorageBucketTool,
        FeeScheduleCleanupTool,
        OpenReportRunStorageTool,
        BridgeDiagnosticsTool,
        CopyShareableLinkTool,
      ],
      multi: true,
    },
  ]);
}
